/**
 * A saved organization poster as the app sees it: the DB row plus the
 * normalized config. Mapping lives here so both adapters agree.
 */
import { normalizeConfig, type PosterConfig, type PosterLang, type PosterTemplate } from "./posterConfig";

export interface OrgPoster {
  id: string;
  organizationId: string;
  /** The printed code in the QR: `/p/<shortCode>`. Immutable. */
  shortCode: string;
  name: string;
  template: PosterTemplate;
  lang: PosterLang;
  questId: string | null;
  /** Relative app path the scan lands on. */
  destinationPath: string;
  config: PosterConfig;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface OrgPosterScanCount {
  posterId: string;
  category: string | null;
  scans: number;
  lastScan: string | null;
}

/** What the editor hands the service on create or save. */
export interface OrgPosterInput {
  name: string;
  template: PosterTemplate;
  lang: PosterLang;
  questId: string | null;
  destinationPath: string;
  config: PosterConfig;
}

export interface OrgPosterRow {
  id: string;
  organization_id: string;
  short_code: string;
  name: string;
  template: string;
  lang: string;
  quest_id: string | null;
  destination_path: string;
  config: unknown;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export const ORG_POSTER_COLUMNS =
  "id, organization_id, short_code, name, template, lang, quest_id, destination_path, config, created_by, created_at, updated_at";

export function mapPosterRow(r: OrgPosterRow): OrgPoster {
  const config = normalizeConfig(r.config);
  return {
    id: r.id,
    organizationId: r.organization_id,
    shortCode: r.short_code,
    name: r.name,
    template: config.template,
    lang: config.lang,
    questId: r.quest_id,
    destinationPath: r.destination_path,
    config,
    createdBy: r.created_by,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}

/** Total scans per poster, from the per-category rows. */
export function scansByPoster(counts: readonly OrgPosterScanCount[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const c of counts) out[c.posterId] = (out[c.posterId] ?? 0) + c.scans;
  return out;
}
